import React, { useState, useEffect, useRef } from "react";
import api from "../../../const/axios";
import Uploader from "./Uploader";
import Toast from "./components/Toast";
import StatChip from "./components/StatChip";
import ProfileActivity from "./components/ProfileActivity";
import ProfileSkeleton from "./components/ProfileSkeleton";
import { useAuthModal } from "../../Auth/AuthModal";
import EventCard from "./components/EventCard";
import ProfileActionButtons from "./components/ProfileActionButtons";
import EditProfileDialog from "./components/dialogs/EditProfileDialog";
import VerificationDialog from "./components/dialogs/VerificationDialog";
import CreateEventDialog from "./components/dialogs/CreateEventDialog";
import EventDetailDialog from "./components/dialogs/EventDetailDialog";
import AuthorLinkDialog from "./components/dialogs/AuthorLinkDialog";
import CreateTeamDialog from "./components/dialogs/CreateTeamDialog";
import InviteUserDialog from "./components/dialogs/InviteUserDialog";
import TeamMembersDialog from "./components/dialogs/TeamMembersDialog";
import ManualGameDialog from "./components/dialogs/ManualGameDialog";
import CreateOrganizerGameDialog from "./components/dialogs/CreateOrganizerGameDialog";
import OrganizerManagementDialog from "./components/dialogs/OrganizerManagementDialog";
import type { ToastState } from "./components/types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { withRouter } from "./../../../utils/withRouter";
import {
  loadingOn, loadingOff, setUserPhoto, getProfile, setProfile,
} from "./../../../redux/actions";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import siteUrl from "./../../../const/const";
import { isFeatureEnabled } from "./../../../services/flagsService";
import history from "./../../../const/history";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Camera, Facebook, Mail, Phone, Plus, UserPlus, Check, X,
  HelpCircle, Layers, Pencil, Brain, Edit3,
  Home, Users, Crown, Star, ShieldCheck, Clock, ShieldX,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePageParam } from "@/hooks/usePageParam";

// ─── Main component ───────────────────────────────────────────────────────────

const Profile: React.FC<any> = ({
  user, profile, params, navigate,
  getProfile: fetchProfile,
  setProfile: updateProfile,
  setUserPhoto: updateUserPhoto,
}) => {
  const { t } = useTranslation();
  const { openLogin } = useAuthModal();

  // UI state
  const [uploadPhoto, setUploadPhoto]     = useState(false);
  const [imageUrl, setImageUrl]           = useState("");
  const [displaySave, setDisplaySave]     = useState(false);
  const [ownProfile, setOwnProfile]       = useState(false);
  const [isOrganizer, setIsOrganizer]     = useState(false);
  const [pageError, setPageError]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const [toast, setToast]                 = useState<ToastState | null>(null);

  // Modals
  const [editModalOpen, setEditModalOpen]           = useState(false);
  const [authorModalOpen, setAuthorModalOpen]       = useState(false);
  const [teamModalOpen, setTeamModalOpen]           = useState(false);
  const [inviteModalOpen, setInviteModalOpen]       = useState(false);
  const [eventModalOpen, setEventModalOpen]         = useState(false);
  const [manualGameModalOpen, setManualGameModalOpen] = useState(false);

  // Edit modal tabs + forms
  const [editTab, setEditTab] = useState<"info" | "password">("info");
  const [infoForm, setInfoForm] = useState({ name: "", surname: "", phone: "" });
  const [infoErrors, setInfoErrors] = useState<Record<string, string>>({});
  const [pwForm, setPwForm]   = useState({ current: "", password: "", confirm: "" });
  const [pwErrors, setPwErrors] = useState<Record<string, string>>({});

  // Author link
  const [authors, setAuthors]           = useState<any[]>([]);
  const [authorSearch, setAuthorSearch] = useState("");
  const [authorValue, setAuthorValue]   = useState<number | null>(null);
  const [authorDropdown, setAuthorDropdown] = useState(false);

  // Teams / invitations
  const [invitations, setInvitations]           = useState<any[]>([]);
  const [teamForm, setTeamForm]                 = useState({ name: "", city: "", information: "" });
  const [selectedTeamId, setSelectedTeamId]     = useState<number | null>(null);
  const [teamMembersModalOpen, setTeamMembersModalOpen] = useState(false);
  const [teamMembersTeam, setTeamMembersTeam]   = useState<any | null>(null);
  const [teamMembersData, setTeamMembersData]   = useState<any[]>([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);

  // Invite user
  const [userSearchList, setUserSearchList]   = useState<any[]>([]);
  const [userSearchText, setUserSearchText]   = useState("");
  const [selectedUserId, setSelectedUserId]   = useState<number | null>(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState("");
  const [userDropdown, setUserDropdown]       = useState(false);

  const [eventsTab, setEventsTab] = useState<"future" | "past">("future");
  // Events page lives in the URL (?page=N) so it survives back/forward navigation
  const { page: eventsPage, setPage: setEventsPage } = usePageParam();
  // Create event (two-step)
  const [eventStep, setEventStep] = useState(1);
  const [eventForm, setEventForm] = useState({
    name: "", date: "", location: "", fee: "", information: "",
    registrationLink: "", gameType: "", registrationType: "", isRated: false,
  });
  const [eventBanner, setEventBanner]           = useState("");
  const [eventBannerMediaId, setEventBannerMediaId] = useState<any>(null);

  // My events (own profile) + profile events (other profile)
  const [myEvents, setMyEvents]           = useState<any[]>([]);
  const [profileEvents, setProfileEvents] = useState<any[]>([]);

  // Event detail + registration modal
  const [detailEvent, setDetailEvent]         = useState<any | null>(null);
  const [registerTeamId, setRegisterTeamId]   = useState<number | null>(null);
  const [isRegistered, setIsRegistered]       = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrations, setRegistrations]     = useState<any[]>([]);

  // Create manual game
  const [gameName, setGameName] = useState("");

  // Organizer management
  const [orgManageOpen, setOrgManageOpen] = useState(false);

  // Verification
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationRequest, setVerificationRequest] = useState<any>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [pendingRefs, setPendingRefs] = useState<any[]>([]);
  const [respondingToken, setRespondingToken] = useState<string | null>(null);

  // Create organizer game
  const [createGameModalOpen, setCreateGameModalOpen] = useState(false);
  const [createGameForm, setCreateGameForm]           = useState({ name: "", date: "", formatId: 0, tournamentId: 0, isRated: false, tierId: null as number | null });
  const [gameFormats, setGameFormats]                 = useState<any[]>([]);
  const [myOrgGames, setMyOrgGames]                   = useState<any[]>([]);
  const [orgGamesTab, setOrgGamesTab]                 = useState<"active" | "completed">("active");
  const [gameTournaments, setGameTournaments]         = useState<any[]>([]);
  const [ratingTiers, setRatingTiers]                 = useState<any[]>([]);
  const [createTournamentLoading, setCreateTournamentLoading] = useState(false);

  const unlistenRef = useRef<(() => void) | null>(null);

  // ── Toast helper ───────────────────────────────────────────────────────────

  const showToast = (message: string, type: ToastState["type"], desc?: string) => {
    setToast({ message, type, desc });
    setTimeout(() => setToast(null), 3500);
  };

  const normalizeTournamentList = (rows: any[]) =>
    (rows ?? [])
      .map((t: any) => ({
        id: Number(t?.id ?? t?.ID ?? 0),
        name: String(t?.name ?? t?.Name ?? "").trim(),
        added: t?.added ?? t?.Added ?? null,
      }))
      .filter((t: any) => t.id > 0 && t.name.length > 0)
      .sort((a: any, b: any) => {
        const ad = a.added ? new Date(a.added).getTime() : 0;
        const bd = b.added ? new Date(b.added).getTime() : 0;
        return bd - ad;
      });

  const fetchOrganizerTournaments = async () => {
    try {
      const res = await api.get("ratings/organizer/tournaments");
      const list = normalizeTournamentList(res.data);
      if (list.length > 0) {
        setGameTournaments(list);
        return;
      }
    } catch {
      // fallback below
    }

    try {
      const fallback = await api.get("tournaments");
      setGameTournaments(normalizeTournamentList(fallback.data));
    } catch {
      setGameTournaments([]);
    }
  };

  // ── Profile loading ────────────────────────────────────────────────────────

  const doGetProfile = () => {
    if (
      user.isLoggedIn &&
      (params.id === undefined || params.id === String(user.data?.id))
    ) {
      setOwnProfile(true);
      fetchProfile(true, null);
    } else if (params.id !== undefined) {
      fetchProfile(false, params.id);
    } else {
      setPageError(true);
    }
  };

  useEffect(() => {
    doGetProfile();
    unlistenRef.current = history.listen((location: any) => {
      if (location.pathname === "/profile") {
        updateProfile({ error: false });
        doGetProfile();
      }
    });
    return () => { if (unlistenRef.current) unlistenRef.current(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVerificationRequest = async () => {
    if (!isFeatureEnabled('ACCOUNT_VERIFICATION')) return;
    setVerificationLoading(true);
    try {
      const res = await api.get("verification-requests/mine");
      setVerificationRequest(res.data);
    } catch {
      setVerificationRequest(null);
    } finally {
      setVerificationLoading(false);
    }
  };

  const fetchPendingRefs = async () => {
    if (!isFeatureEnabled('ACCOUNT_VERIFICATION')) return;
    try {
      const res = await api.get("verification-requests/pending-for-me");
      setPendingRefs(res.data ?? []);
    } catch {
      setPendingRefs([]);
    }
  };

  const handleRefRespond = async (token: string, approve: boolean) => {
    setRespondingToken(token);
    try {
      await api.put(`verification-requests/reference/${token}/respond`, { approve });
      showToast(approve ? "Zəmanət verdiniz" : "Rədd etdiniz", "success");
      fetchPendingRefs();
    } catch (e: any) {
      showToast(e?.response?.data?.message ?? "Xəta baş verdi", "error");
    } finally {
      setRespondingToken(null);
    }
  };

  useEffect(() => {
    if (ownProfile) {
      fetchInvitations();
      fetchMyEvents();
      fetchVerificationRequest();
      fetchPendingRefs();
      api.get("rating-tiers").then(r => setRatingTiers(r.data || [])).catch(() => {});
      api.get("users/organizer/me")
        .then((res) => {
          const org = !!res.data?.isOrganizer;
          setIsOrganizer(org);
          if (org) {
            api.get("game-formats").then(r => setGameFormats(r.data)).catch(() => {});
            api.get("organizer-games/mine").then(r => setMyOrgGames(r.data)).catch(() => {});
            fetchOrganizerTournaments();
          }
        })
        .catch(() => setIsOrganizer(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownProfile]);

  // Load events for other profiles when profile.id is available
  useEffect(() => {
    if (!ownProfile && profile?.id) {
      api.get(`events/user/${profile.id}`)
        .then(res => setProfileEvents(res.data))
        .catch(() => {});
    }
  }, [ownProfile, profile?.id]);

  // Populate info form when edit modal opens
  useEffect(() => {
    if (editModalOpen && profile) {
      setInfoForm({
        name:     profile.name     || "",
        surname:  profile.surname  || "",
        phone:    profile.phone    ? profile.phone.substring(5) : "",
      });
      setInfoErrors({});
      setEditTab("info");
      setPwForm({ current: "", password: "", confirm: "" });
      setPwErrors({});
    }
  }, [editModalOpen, profile]);

  // Check registration status + fetch registrations when event detail modal opens
  useEffect(() => {
    if (!detailEvent) return;

    api.get(`events/${detailEvent.id}/registrations`)
      .then(res => setRegistrations(res.data))
      .catch(() => {});

    if (!user.isLoggedIn) return;
    api.get(`events/${detailEvent.id}/myregistration`)
      .then(res => {
        setIsRegistered(res.data.isRegistered);
        if (res.data.isRegistered && res.data.registration?.team) {
          setRegisterTeamId(res.data.registration.team.id);
        }
      })
      .catch(() => {});
  }, [detailEvent, user.isLoggedIn]);

  // ── Invitations ────────────────────────────────────────────────────────────

  const fetchInvitations = async () => {
    try {
      const res = await api.get("teams/invitations");
      setInvitations(res.data);
    } catch {
      console.error("Failed to fetch invitations");
    }
  };

  const fetchMyEvents = async () => {
    try {
      const res = await api.get("events/mine");
      setMyEvents(res.data);
    } catch {
      console.error("Failed to fetch my events");
    }
  };

  const handleInviteResponse = async (invitationId: number, accept: boolean) => {
    try {
      const res = await api.post("teams/respond", JSON.stringify({ invitationId, accept }));
      if (res.data.success) {
        showToast(res.data.message, "success");
        fetchInvitations();
        fetchProfile(true, null);
      }
    } catch {
      showToast("Xəta baş verdi", "error");
    }
  };

  // ── Photo upload ───────────────────────────────────────────────────────────

  const photoIsUploaded = (url: string) => {
    setUploadPhoto(false);
    setImageUrl(url);
    setDisplaySave(true);
  };

  const cancelPhotoChange = () => {
    setDisplaySave(false);
    setUploadPhoto(false);
    setImageUrl("");
  };

  const saveUserPhoto = async () => {
    setLoading(true);
    try {
      const res = await api.put("users/photo", JSON.stringify({ id: profile.id, filename: imageUrl }));
      if (res.data.success) {
        showToast("Əməliyyat uğurlu oldu", "success", res.data.message);
        const changedUser = { ...profile };
        changedUser.photo = `images/users/${imageUrl}`;
        updateUserPhoto(changedUser.photo);
        updateProfile(changedUser);
        cancelPhotoChange();
      } else {
        showToast("Əməliyyat uğursuz oldu", "info", res.data.message);
        cancelPhotoChange();
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Edit profile ───────────────────────────────────────────────────────────

  const validateInfoForm = () => {
    const errs: Record<string, string> = {};
    if (!infoForm.name.trim())            errs.name     = t("auth.nameRequired");
    else if (infoForm.name.trim().length < 2) errs.name = t("auth.nameMinLength");
    if (!infoForm.surname.trim())              errs.surname  = t("auth.surnameRequired");
    else if (infoForm.surname.trim().length < 2) errs.surname = t("auth.surnameMinLength");
    setInfoErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmitInfo = async () => {
    if (!validateInfoForm()) return;
    setLoading(true);
    const obj = {
      name:     infoForm.name,
      surname:  infoForm.surname,
      email:    profile.email,
      phone:    infoForm.phone ? "+994" + infoForm.phone : null,
      id:       profile.id,
    };
    try {
      const res = await api.put(`users/${profile.id}`, JSON.stringify(obj));
      if (res.data.success) {
        showToast("Əməliyyat uğurlu oldu", "success", res.data.message);
        updateProfile({ ...profile, ...obj });
        setEditModalOpen(false);
      } else {
        showToast("Əməliyyat uğursuz oldu", "info", res.data.message);
      }
    } catch {
      showToast("Xəta baş verdi", "error");
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordForm = () => {
    const errs: Record<string, string> = {};
    if (!pwForm.current)  errs.current  = t("auth.passwordRequired");
    if (!pwForm.password) errs.password = t("auth.passwordRequired");
    else if (pwForm.password.length < 8) errs.password = t("auth.passwordMinLength");
    if (!pwForm.confirm)  errs.confirm  = t("auth.confirmPasswordRequired");
    else if (pwForm.confirm !== pwForm.password) errs.confirm = "Şifrələr uyğun gəlmir";
    setPwErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmitPassword = async () => {
    if (!validatePasswordForm()) return;
    setLoading(true);
    try {
      const res = await api.put("users/password", JSON.stringify({ ...pwForm, id: profile.id }));
      if (res.data.success) {
        showToast("Əməliyyat uğurlu oldu", "success", res.data.message);
        setEditModalOpen(false);
      } else {
        showToast("Əməliyyat uğursuz oldu", "info", res.data.message);
      }
    } catch {
      showToast("Xəta baş verdi", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Author link ────────────────────────────────────────────────────────────

  const handleAuthorSearch = async (value: string) => {
    setAuthorSearch(value);
    setAuthorValue(null);
    if (value.length > 1) {
      const res = await api.get(`authors?search=${value}`);
      setAuthors(res.data);
      setAuthorDropdown(true);
    } else {
      setAuthors([]);
      setAuthorDropdown(false);
    }
  };

  const linkAuthor = async () => {
    if (!authorValue) {
      showToast("Seçim etməmisiniz", "info", "Sualları öz adınıza keçirmək üçün ilk öncə müəllif seçməlisiniz.");
      return;
    }
    const author = authors.find(a => a.id === authorValue);
    const obj = {
      email:     user.data.email,
      isHandled: false,
      message:   `${user.data.id} ID-yə sahib, ${user.data.name} ${user.data.surname} adlı istifadəçi və ${authorValue} ID-yə sahib, ${author.fullname} adlı müəllifə bağlanılmalıdır.`,
      name:      user.data.name,
      phone:     user.data.phone,
      surname:   user.data.surname,
      topic:     "Müəllif təyini",
    };
    await api.post("contacts", JSON.stringify(obj));
    showToast("Təşəkkürlər!", "success", "Müraciətinizə ən qısa zamanda baxılacaq");
    setAuthorModalOpen(false);
  };

  // ── Create team ────────────────────────────────────────────────────────────

  const handleCreateTeam = async () => {
    if (!teamForm.name.trim() || !teamForm.city.trim()) {
      showToast("Zəruri sahələri doldurun", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("teams", JSON.stringify(teamForm));
      if (res.status === 201) {
        showToast("Komanda yaradıldı", "success");
        setTeamModalOpen(false);
        setTeamForm({ name: "", city: "", information: "" });
        fetchProfile(true, null);
      }
    } catch {
      showToast("Komanda yaradıla bilmədi", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Team members / captaincy / primary ────────────────────────────────────

  const openTeamMembers = async (team: any) => {
    setTeamMembersTeam(team);
    setTeamMembersModalOpen(true);
    setTeamMembersLoading(true);
    try {
      const res = await api.get(`teams/${team.id}`);
      const members = (res.data.team_users ?? []).filter((tu: any) => tu.status === 1);
      setTeamMembersData(members);
    } catch {
      setTeamMembersData([]);
    } finally {
      setTeamMembersLoading(false);
    }
  };

  const handleTransferCaptaincy = async (teamId: number, userId: number) => {
    try {
      await api.put(`teams/${teamId}/captain/${userId}`);
      showToast("Kapitanlıq verildi", "success");
      // Refresh members list
      const res = await api.get(`teams/${teamId}`);
      setTeamMembersData((res.data.team_users ?? []).filter((tu: any) => tu.status === 1));
      fetchProfile(true, null);
    } catch {
      showToast("Xəta baş verdi", "error");
    }
  };

  const handleRemoveMember = async (teamId: number, userId: number) => {
    try {
      const res = await api.delete(`teams/${teamId}/members/${userId}`);
      if (res.data.success) {
        showToast(res.data.message ?? "Oyunçu çıxarıldı", "success");
        // Refresh members list in the dialog
        const updated = await api.get(`teams/${teamId}`);
        setTeamMembersData((updated.data.team_users ?? []).filter((tu: any) => tu.status === 1));
      } else {
        showToast(res.data.message ?? "Xəta baş verdi", "error");
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Xəta baş verdi";
      showToast(msg, "error");
    }
  };

  const handleDeleteTeam = async (teamId: number, confirmName: string) => {
    try {
      const res = await api.delete(`teams/${teamId}`, { data: { confirmName } });
      if (res.data.success) {
        showToast("Komanda silindi", "success");
        setTeamMembersModalOpen(false);
        fetchProfile(true, null);
      } else {
        showToast(res.data.message ?? "Xəta baş verdi", "error");
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Xəta baş verdi";
      showToast(msg, "error");
    }
  };

  const handleSetPrimary = async (teamId: number) => {
    try {
      await api.put(`teams/primary/${teamId}`);
      fetchProfile(true, null);
    } catch {
      showToast("Xəta baş verdi", "error");
    }
  };

  // ── Invite user ────────────────────────────────────────────────────────────

  const handleUserSearch = async (value: string) => {
    setUserSearchText(value);
    setSelectedUserId(null);
    setSelectedUserLabel("");
    if (value.length > 2) {
      const res = await api.get(`users?search=${value}&perPage=10`);
      setUserSearchList(res.data.users || []);
      setUserDropdown(true);
    } else {
      setUserSearchList([]);
      setUserDropdown(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!selectedUserId) {
      showToast("Oyunçu seçmək mütləqdir", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("teams/invite", JSON.stringify({ teamId: selectedTeamId, userId: selectedUserId }));
      if (res.data.success) {
        showToast(res.data.message, "success");
        setInviteModalOpen(false);
        setSelectedUserId(null);
        setSelectedUserLabel("");
        setUserSearchText("");
      } else {
        showToast(res.data.message, "info");
      }
    } catch (err) {
      console.log(err.response.data, 'error here')
      showToast(err.response.data || "Dəvət göndərilə bilmədi", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Create event (two-step) ────────────────────────────────────────────────

  const resetEventModal = () => {
    setEventBanner("");
    setEventBannerMediaId(null);
    setEventForm({ name: "", date: "", location: "", fee: "", information: "", registrationLink: "", gameType: "", registrationType: "", isRated: false });
    setEventStep(1);
  };

  const handleEventStep1Next = () => {
    if (!eventForm.name.trim() || !eventForm.date || !eventForm.location.trim()) {
      showToast("Zəruri sahələri doldurun", "error");
      return;
    }
    setEventStep(2);
  };

  const handleCreateEvent = async () => {
    if (!eventForm.gameType) {
      showToast("Oyun növünü seçin", "error");
      return;
    }
    if (!eventForm.registrationType) {
      showToast("Qeydiyyat növünü seçin", "error");
      return;
    }
    if (eventForm.registrationType === "link" && !eventForm.registrationLink.trim()) {
      showToast("Qeydiyyat linkini daxil edin", "error");
      return;
    }
    setLoading(true);
    try {
      const obj = {
        ...eventForm,
        media_ID: eventBannerMediaId,
        date: new Date(eventForm.date).toISOString(),
        fee: eventForm.fee ? parseFloat(eventForm.fee) : null,
        isRated: eventForm.isRated,
      };
      const res = await api.post("events", JSON.stringify(obj));
      if (res.status === 201) {
        showToast("Tədbir göndərildi", "success", "Tədbiriniz administrator tərəfindən təsdiqləndikdən sonra ana səhifədə görünəcək.");
        setEventModalOpen(false);
        resetEventModal();
        fetchMyEvents();
      }
    } catch {
      showToast("Xəta baş verdi", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Event registration ─────────────────────────────────────────────────────

  const handleRegister = async () => {
    if (!detailEvent) return;
    if (!user.isLoggedIn) {
      openLogin();
      return;
    }
    if (detailEvent.gameType === "team" && !registerTeamId) {
      showToast("Komanda seçin", "error");
      return;
    }
    setRegistrationLoading(true);
    try {
      const body = detailEvent.gameType === "team" ? { teamId: registerTeamId } : {};
      const res = await api.post(`events/${detailEvent.id}/register`, JSON.stringify(body));
      if (res.data.success) {
        showToast(res.data.message, "success");
        setIsRegistered(true);
        api.get(`events/${detailEvent.id}/registrations`).then(r => setRegistrations(r.data)).catch(() => {});
      } else {
        showToast(res.data.message, "info");
      }
    } catch {
      showToast("Xəta baş verdi", "error");
    } finally {
      setRegistrationLoading(false);
    }
  };

  // ── Create manual game ─────────────────────────────────────────────────────

  const handleCreateManualGame = async () => {
    if (!gameName.trim()) {
      showToast("Oyun adını daxil edin", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("tournaments/create", JSON.stringify({ name: gameName }));
      if (res.status === 201) {
        setManualGameModalOpen(false);
        navigate(`/game/${res.data.id}`, { state: { gameName } });
        showToast("Oyun yaradıldı", "success");
      }
    } catch {
      showToast("Xəta baş verdi", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTournamentForOrgGame = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      showToast("Turnir adını daxil edin", "error");
      return;
    }

    setCreateTournamentLoading(true);
    try {
      const res = await api.post("tournaments/create", JSON.stringify({ name: trimmed }));
      const createdId = Number(res.data?.id ?? res.data?.ID ?? 0);
      await fetchOrganizerTournaments();
      if (createdId > 0) {
        setCreateGameForm((f) => ({ ...f, tournamentId: createdId }));
      }
      showToast("Turnir yaradıldı", "success");
    } catch {
      showToast("Turnir yaradıla bilmədi", "error");
    } finally {
      setCreateTournamentLoading(false);
    }
  };

  // ── Create organizer game ──────────────────────────────────────────────────

  const handleCreateOrgGame = async () => {
    if (!createGameForm.name.trim()) {
      showToast("Oyun adını daxil edin", "error");
      return;
    }
    if (!createGameForm.date) {
      showToast("Tarixi seçin", "error");
      return;
    }
    if (!createGameForm.formatId) {
      showToast("Format seçin", "error");
      return;
    }
    if (!createGameForm.tournamentId) {
      showToast("Turnir seçin", "error");
      return;
    }
    if (createGameForm.isRated && !createGameForm.tierId) {
      showToast("Reytinqli oyun üçün tier seçin", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("organizer-games", JSON.stringify({
        name: createGameForm.name,
        date: new Date(createGameForm.date).toISOString(),
        formatId: createGameForm.formatId,
        tournamentId: createGameForm.tournamentId,
        isRated: createGameForm.isRated,
        tierId: createGameForm.isRated ? createGameForm.tierId : null,
      }));
      if (res.status === 201) {
        setCreateGameModalOpen(false);
        setCreateGameForm({ name: "", date: "", formatId: 0, tournamentId: 0, isRated: false, tierId: null });
        showToast(
          "Oyun yaradıldı", "success",
          createGameForm.isRated
            ? "Admin təsdiqləyəndən sonra masa açılacaq. Tier admin tərəfindən təsdiqlənəcək."
            : "Admin təsdiqləyəndən sonra masa açılacaq."
        );
        api.get("organizer-games/mine").then(r => setMyOrgGames(r.data)).catch(() => {});
      }
    } catch {
      showToast("Xəta baş verdi", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── 404 ────────────────────────────────────────────────────────────────────

  if (profile.error || pageError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16 flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <p className="text-8xl font-black text-gray-200">404</p>
          <p className="text-lg font-semibold text-gray-500 dark:text-gray-400">Axtardığınız səhifə mövcud deyil</p>
          <Link to="/">
            <Button className="mt-2 gap-2">
              <Home className="w-4 h-4" />
              Əsas səhifəyə qayıt
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (profile.loading) return <ProfileSkeleton />;

  // ── Avatar src ─────────────────────────────────────────────────────────────

  const avatarSrc = imageUrl
    ? `${siteUrl}images/temp/${imageUrl}`
    : profile?.photo
      ? `${siteUrl}${profile.photo}`
      : `${siteUrl}images/avatar.jpg`;

  const author = profile?.author;

  // ── Derived events ─────────────────────────────────────────────────────────

  const displayEvents = ownProfile ? myEvents : profileEvents;
  const hasEvents = displayEvents.length > 0;

  // Events tab + pagination state
  const EVENTS_PER_PAGE = 6;

  const now = Date.now();
  const futureEvents = displayEvents.filter((ev: any) => new Date(ev.date).getTime() >= now);
  const pastEvents   = displayEvents.filter((ev: any) => new Date(ev.date).getTime() <  now)
                                    .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const activeList  = eventsTab === "future" ? futureEvents : pastEvents;
  const totalPages  = Math.ceil(activeList.length / EVENTS_PER_PAGE);
  const pagedEvents = activeList.slice((eventsPage - 1) * EVENTS_PER_PAGE, eventsPage * EVENTS_PER_PAGE);

  const switchTab = (tab: "future" | "past") => { setEventsTab(tab); setEventsPage(1); };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>
          {profile ? `${profile.name} ${profile.surname}` : "Profil"} | 3sual.az
        </title>
      </Helmet>

      <Toast toast={toast} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-start">

          {/* ── Left sidebar ──────────────────────────────────────────────── */}
          <div className="space-y-3">
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-6 flex flex-col items-center text-center gap-3">

              {/* Avatar */}
              {uploadPhoto ? (
                <div className="flex flex-col items-center gap-3">
                  <Uploader imageUpload={photoIsUploaded} />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUploadPhoto(false)}
                    className="w-full"
                  >
                    Ləğv et
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 w-full">
                  <div
                    className="relative group cursor-pointer"
                    onClick={() => ownProfile && !displaySave && setUploadPhoto(true)}
                  >
                    <img
                      src={avatarSrc}
                      alt="profile"
                      className="w-24 h-24 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-800"
                    />
                    {ownProfile && !displaySave && (
                      <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Photo save/cancel */}
                  {displaySave && (
                    <div className="flex gap-2 w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={cancelPhotoChange}
                      >
                        Ləğv et
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={saveUserPhoto}
                        disabled={loading}
                      >
                        Yadda saxla
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Name */}
              {profile && (
                <div>
                  <p className="font-black text-gray-900 dark:text-gray-50 text-lg leading-tight">
                    {profile.name} {profile.surname}
                  </p>
                </div>
              )}

              {/* Facebook */}
              {profile?.facebook && (
                <a
                  href={profile.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1877F2] hover:text-[#166FE5] transition-colors"
                >
                  <Facebook className="w-6 h-6" />
                </a>
              )}

              {/* Edit profile button */}
              {ownProfile && !uploadPhoto && !displaySave && (
                <>
                  <Separator className="w-full" />
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => setEditModalOpen(true)}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    {t("profile.title")}
                  </Button>
                </>
              )}
            </div>

            {/* Contact info (own profile only) */}
            {ownProfile && profile && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-4 space-y-3">
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Əlaqə</p>
                <div className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-200">
                  <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="truncate">{profile.email}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-200">
                  <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span>{profile.phone || "—"}</span>
                </div>
              </div>
            )}
          </div>

          {/* ── Right column ──────────────────────────────────────────────── */}
          <div className="space-y-4">

            {/* Author stats */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
              <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Statİstİka</p>
              <div className="grid grid-cols-2 gap-2">
                <StatChip
                  label="Redaktə"
                  count={author?.editor ?? 0}
                  icon={<Pencil className="w-4 h-4" />}
                  to={author?.editor ? `/authors/${author.id}/edits` : undefined}
                />
                <StatChip
                  label="Nə? Harada? Nə zaman?"
                  count={author?.nhn ?? 0}
                  icon={<HelpCircle className="w-4 h-4" />}
                  to={author?.nhn ? `/authors/${author.id}/questions` : undefined}
                />
                <StatChip
                  label="Mövzu"
                  count={author?.theme ?? 0}
                  icon={<Layers className="w-4 h-4" />}
                  to={author?.theme ? `/authors/${author.id}/themes` : undefined}
                />
                <StatChip
                  label="Breyn Rinq"
                  count={author?.other ?? 0}
                  icon={<Brain className="w-4 h-4" />}
                />
              </div>
            </div>

            {/* Own ratings + comments */}
            {ownProfile && isFeatureEnabled('PROFILE_ACTIVITY') && <ProfileActivity />}

            {/* Teams + invitations */}
            {isFeatureEnabled('PROFILE_TEAMS') && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Komandalar</p>
                {ownProfile && (
                  <button
                    onClick={() => setTeamModalOpen(true)}
                    className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Yeni komanda
                  </button>
                )}
              </div>

              {/* Pending invitations */}
              {ownProfile && invitations.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-blue-500 mb-2 dark:text-indigo-300">Dəvətlər</p>
                  <div className="space-y-2">
                    {invitations.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between py-2 px-3 bg-blue-50 dark:bg-blue-950 rounded-xl">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{inv.teamName}</span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleInviteResponse(inv.id, true)}
                            className="w-7 h-7 rounded-lg bg-green-100 hover:bg-green-200 flex items-center justify-center transition-colors"
                          >
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          </button>
                          <button
                            onClick={() => handleInviteResponse(inv.id, false)}
                            className="w-7 h-7 rounded-lg bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors"
                          >
                            <X className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Separator className="mt-3" />
                </div>
              )}

              {profile?.teams?.length ? (
                <div className="divide-y divide-gray-50">
                  {profile.teams.map((team: any) => {
                    const isPrimary = profile.primaryTeamId === team.id;
                    return (
                      <div key={team.id} className="flex items-center justify-between py-2.5 gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{team.name}</p>
                            {team.isCaptain && (
                              <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            )}
                            {isPrimary && (
                              <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:text-indigo-300">Əsas</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{team.count} oyunçu</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {ownProfile && !isPrimary && (
                            <button
                              onClick={() => handleSetPrimary(team.id)}
                              className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:border-blue-300 dark:hover:border-blue-900 hover:text-blue-500 transition-colors dark:hover:text-indigo-200"
                              title="Əsas komanda et"
                            >
                              <Star className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => openTeamMembers(team)}
                            className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-300 dark:hover:border-blue-900 hover:text-blue-500 transition-colors dark:hover:text-indigo-200"
                            title="Üzvlər"
                          >
                            <Users className="w-3.5 h-3.5" />
                          </button>
                          {ownProfile && team.isCaptain && (
                            <button
                              onClick={() => { setSelectedTeamId(team.id); setInviteModalOpen(true); }}
                              className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-300 dark:hover:border-blue-900 hover:text-blue-500 transition-colors dark:hover:text-indigo-200"
                              title="Oyunçu dəvət et"
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {ownProfile ? "Hələ ki, heç bir komandada deyilsiniz." : "Bu istifadəçi heç bir komandada deyil."}
                </p>
              )}
            </div>
            )}

            {/* Events section */}
            {isFeatureEnabled('PROFILE_EVENTS') && (ownProfile || hasEvents) && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                    {ownProfile ? "Tədbİrlərim" : "Tədbİrlər"}
                  </p>
                  {ownProfile && (
                    <button
                      onClick={() => setEventModalOpen(true)}
                      className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Yeni
                    </button>
                  )}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-4">
                  {(["future", "past"] as const).map(tab => (
                    <button
                      key={tab}
                      onClick={() => switchTab(tab)}
                      className={cn(
                        "flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200",
                        eventsTab === tab
                          ? "bg-white dark:bg-gray-900 shadow-sm text-gray-900 dark:text-gray-50"
                          : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300"
                      )}
                    >
                      {tab === "future" ? `Aktiv (${futureEvents.length})` : `Keçmiş (${pastEvents.length})`}
                    </button>
                  ))}
                </div>

                {/* Event list */}
                {pagedEvents.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3">
                    {pagedEvents.map((ev: any) => (
                      <EventCard
                        key={ev.id}
                        event={ev}
                        showStatus={ownProfile}
                        onClick={() => {
                          setDetailEvent(ev);
                          setRegisterTeamId(null);
                          setIsRegistered(false);
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    {eventsTab === "future"
                      ? ownProfile ? "Hələ ki, planlaşdırılmış tədbir yoxdur." : "Planlaşdırılmış tədbir yoxdur."
                      : ownProfile ? "Keçmiş tədbir tapılmadı."              : "Keçmiş tədbir yoxdur."}
                  </p>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                    <button
                      onClick={() => setEventsPage(eventsPage - 1)}
                      disabled={eventsPage === 1}
                      className="flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:text-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Əvvəlki
                    </button>
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                      {eventsPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setEventsPage(eventsPage + 1)}
                      disabled={eventsPage === totalPages}
                      className="flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:text-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      Növbəti
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}


            {/* Organizer management entry — visible to own-profile users whether organizer or not */}
            {ownProfile && !isOrganizer && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Təşkilatçı</p>
                  <button
                    onClick={() => setOrgManageOpen(true)}
                    className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Təşkilatçı ol
                  </button>
                </div>
              </div>
            )}

            {/* Organizer games section */}
            {isFeatureEnabled('PROFILE_GAMES') && ownProfile && isOrganizer && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Oyunlarım</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setOrgManageOpen(true)}
                      className="flex items-center gap-1 text-xs font-semibold text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300 transition-colors"
                    >
                      <Home className="w-3.5 h-3.5" />
                      Təşkilatçı
                    </button>
                    <button
                      onClick={() => setCreateGameModalOpen(true)}
                      className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Yeni
                    </button>
                  </div>
                </div>
                {/* Tabs */}
                <div className="flex gap-1 mb-3 border-b border-gray-100 dark:border-gray-800">
                  {(["active", "completed"] as const).map(tab => (
                    <button
                      key={tab}
                      onClick={() => setOrgGamesTab(tab)}
                      className={cn(
                        "pb-2 px-1 text-xs font-semibold transition-colors border-b-2 -mb-px",
                        orgGamesTab === tab
                          ? "border-blue-500 text-blue-600 dark:text-indigo-300"
                          : "border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300"
                      )}
                    >
                      {tab === "active" ? "Aktiv" : "Tamamlanmış"}
                      <span className={cn(
                        "ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full",
                        orgGamesTab === tab ? "bg-blue-100 text-blue-600 dark:text-indigo-300" : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
                      )}>
                        {tab === "active"
                          ? myOrgGames.filter((g: any) => !g.isCompleted).length
                          : myOrgGames.filter((g: any) => g.isCompleted).length}
                      </span>
                    </button>
                  ))}
                </div>
                {(() => {
                  const filtered = myOrgGames.filter((g: any) =>
                    orgGamesTab === "active" ? !g.isCompleted : g.isCompleted
                  );
                  if (filtered.length === 0) {
                    return (
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        {orgGamesTab === "active"
                          ? "Aktiv oyun yoxdur."
                          : "Tamamlanmış oyun yoxdur."}
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      {filtered.map((g: any) => (
                        <div
                          key={g.id}
                          className={cn(
                            "flex items-center justify-between py-2.5 px-3 rounded-xl border transition-colors",
                            g.isCompleted
                              ? "border-purple-100 bg-purple-50/30 hover:bg-purple-50 cursor-pointer"
                              : g.isApproved
                                ? "border-green-100 bg-green-50/40 hover:bg-green-50 dark:bg-green-950 cursor-pointer"
                                : "border-gray-100 dark:border-gray-800 bg-gray-50/40"
                          )}
                          onClick={() => g.isApproved && navigate(`/games/${g.slug}`)}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{g.name}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{g.format?.name}</p>
                            {g.tournament?.name ? (
                              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">Turnir: {g.tournament.name}</p>
                            ) : null}
                          </div>
                          <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                            <span className={cn(
                              "text-[10px] font-bold px-2 py-0.5 rounded-full",
                              g.isCompleted
                                ? "bg-purple-100 text-purple-700"
                                : g.isApproved
                                  ? "bg-green-100 text-green-700"
                                  : "bg-amber-100 text-amber-700"
                            )}>
                              {g.isCompleted ? "Tamamlandı" : g.isApproved ? "Təsdiqlənib" : "Gözlənilir"}
                            </span>
                            {g.isRated && (
                              <span className={cn(
                                "text-[9px] font-bold px-1.5 py-0.5 rounded-full",
                                g.tierStatus === "approved" ? "bg-amber-100 text-amber-700"
                                  : g.tierStatus === "rejected" ? "bg-red-100 text-red-600"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                              )}>
                                {g.tierStatus === "approved" ? "Reytinqli" : g.tierStatus === "rejected" ? "Tier rədd" : "Tier admin təsdiqini gözləyir"}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Verification section (own profile, not yet verified) */}
            {isFeatureEnabled('ACCOUNT_VERIFICATION') && ownProfile && !profile?.isVerified && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Hesab təsdiqİ</p>
                  {!verificationRequest && !verificationLoading && (
                    <button
                      onClick={() => setVerificationDialogOpen(true)}
                      className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Müraciət et
                    </button>
                  )}
                </div>

                {verificationLoading ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">Yüklənir...</p>
                ) : !verificationRequest ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Hesabınızı təsdiqləmək üçün iki təsdiqlənmiş istifadəçinin zəmanəti tələb olunur.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {/* Status badge */}
                    <div className="flex items-center gap-2">
                      {verificationRequest.status === "PendingReferences" && (
                        <>
                          <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                          <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Zəmanətçilərin cavabı gözlənilir</span>
                        </>
                      )}
                      {verificationRequest.status === "PendingAdmin" && (
                        <>
                          <Clock className="w-4 h-4 text-blue-500 shrink-0 dark:text-indigo-300" />
                          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Admin nəzərdindədir</span>
                        </>
                      )}
                      {verificationRequest.status === "Rejected" && (
                        <>
                          <ShieldX className="w-4 h-4 text-red-400 shrink-0" />
                          <span className="text-sm font-medium text-red-500 dark:text-red-400">Müraciət rədd edildi</span>
                        </>
                      )}
                    </div>

                    {/* Admin note if rejected */}
                    {verificationRequest.status === "Rejected" && verificationRequest.adminNote && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
                        Səbəb: {verificationRequest.adminNote}
                      </p>
                    )}

                    {/* References */}
                    {(verificationRequest.references ?? []).map((ref: any, i: number) => (
                      <div key={ref.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-300">
                          {ref.referee.name} {ref.referee.surname}
                        </span>
                        <span className={
                          ref.status === "Approved" ? "text-green-600 dark:text-green-400 font-medium" :
                          ref.status === "Declined" ? "text-red-500 dark:text-red-400 font-medium" :
                          "text-amber-500 dark:text-amber-400"
                        }>
                          {ref.status === "Approved" ? "Təsdiqlədi" : ref.status === "Declined" ? "Rədd etdi" : "Gözlənilir"}
                        </span>
                      </div>
                    ))}

                    {/* Re-apply button when rejected */}
                    {verificationRequest.status === "Rejected" && (() => {
                      const reviewedAt = verificationRequest.reviewedAt
                        ? new Date(verificationRequest.reviewedAt)
                        : new Date(verificationRequest.requestedAt);
                      const unlocksAt = new Date(reviewedAt);
                      unlocksAt.setMonth(unlocksAt.getMonth() + 6);
                      const locked = unlocksAt > new Date();
                      return locked ? (
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                          Yenidən müraciət: {unlocksAt.toLocaleDateString("az-AZ")}
                        </p>
                      ) : (
                        <button
                          onClick={() => setVerificationDialogOpen(true)}
                          className="mt-1 flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Yenidən müraciət et
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Pending reference requests for verified users */}
            {isFeatureEnabled('ACCOUNT_VERIFICATION') && ownProfile && profile?.isVerified && pendingRefs.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
                  Zəmanət müraciətləri
                  <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:text-indigo-300">{pendingRefs.length}</span>
                </p>
                <div className="space-y-3">
                  {pendingRefs.map((ref: any) => (
                    <div key={ref.token} className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 space-y-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                          {ref.applicant.name} {ref.applicant.surname}
                        </p>
                        {ref.note && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ref.note}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRefRespond(ref.token, false)}
                          disabled={respondingToken === ref.token}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 transition-colors disabled:opacity-50"
                        >
                          <X className="w-3.5 h-3.5" />
                          Rədd et
                        </button>
                        <button
                          onClick={() => handleRefRespond(ref.token, true)}
                          disabled={respondingToken === ref.token}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Zəmanət ver
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Verified badge (own profile) */}
            {isFeatureEnabled('ACCOUNT_VERIFICATION') && ownProfile && profile?.isVerified && (
              <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-2xl px-6 py-4">
                <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-700 dark:text-green-300">Təsdiqlənmiş hesab</p>
                </div>
              </div>
            )}

            <ProfileActionButtons
              ownProfile={ownProfile}
              hasAuthor={!!author}
              onOpenAuthorLink={() => setAuthorModalOpen(true)}
            />
          </div>
        </div>
      </div>

      <EditProfileDialog
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        t={t}
        editTab={editTab}
        setEditTab={setEditTab}
        infoForm={infoForm}
        setInfoForm={setInfoForm}
        infoErrors={infoErrors}
        pwForm={pwForm}
        setPwForm={setPwForm}
        pwErrors={pwErrors}
        loading={loading}
        isVerified={!!profile?.isVerified}
        onSubmitInfo={handleSubmitInfo}
        onSubmitPassword={handleSubmitPassword}
      />

      <AuthorLinkDialog
        open={authorModalOpen}
        onOpenChange={setAuthorModalOpen}
        authorSearch={authorSearch}
        setAuthorSearch={setAuthorSearch}
        authors={authors}
        authorDropdown={authorDropdown}
        setAuthorDropdown={setAuthorDropdown}
        setAuthorValue={setAuthorValue}
        authorValue={authorValue}
        onSearch={handleAuthorSearch}
        onConfirm={linkAuthor}
      />

      <CreateTeamDialog
        open={teamModalOpen}
        onOpenChange={(v) => {
          setTeamModalOpen(v);
          if (!v) setTeamForm({ name: "", city: "", information: "" });
        }}
        teamForm={teamForm}
        setTeamForm={setTeamForm}
        loading={loading}
        onCreate={handleCreateTeam}
      />

      <InviteUserDialog
        open={inviteModalOpen}
        onOpenChange={(v) => {
          setInviteModalOpen(v);
          if (!v) {
            setSelectedUserId(null);
            setSelectedUserLabel("");
            setUserSearchText("");
            setUserSearchList([]);
          }
        }}
        selectedUserLabel={selectedUserLabel}
        userSearchText={userSearchText}
        setSelectedUserLabel={setSelectedUserLabel}
        userSearchList={userSearchList}
        userDropdown={userDropdown}
        setUserDropdown={setUserDropdown}
        setSelectedUserId={setSelectedUserId}
        setUserSearchText={setUserSearchText}
        selectedUserId={selectedUserId}
        loading={loading}
        onSearch={handleUserSearch}
        onSend={handleSendInvitation}
      />

      <TeamMembersDialog
        open={teamMembersModalOpen}
        onOpenChange={(v) => {
          setTeamMembersModalOpen(v);
          if (!v) {
            setTeamMembersData([]);
            setTeamMembersTeam(null);
          }
        }}
        teamMembersTeam={teamMembersTeam}
        teamMembersLoading={teamMembersLoading}
        teamMembersData={teamMembersData}
        ownProfile={ownProfile}
        profileId={profile?.id}
        onTransferCaptaincy={handleTransferCaptaincy}
        onRemoveMember={handleRemoveMember}
        onDeleteTeam={handleDeleteTeam}
      />

      <CreateEventDialog
        open={eventModalOpen}
        onOpenChange={(v) => {
          setEventModalOpen(v);
          if (!v) resetEventModal();
        }}
        eventStep={eventStep}
        setEventStep={setEventStep}
        eventForm={eventForm}
        setEventForm={setEventForm}
        eventBanner={eventBanner}
        setEventBanner={setEventBanner}
        setEventBannerMediaId={setEventBannerMediaId}
        loading={loading}
        onNext={handleEventStep1Next}
        onCreate={handleCreateEvent}
      />

      <ManualGameDialog
        open={manualGameModalOpen}
        onOpenChange={(v) => {
          setManualGameModalOpen(v);
          if (!v) setGameName("");
        }}
        gameName={gameName}
        setGameName={setGameName}
        loading={loading}
        onCreate={handleCreateManualGame}
      />

      <CreateOrganizerGameDialog
        open={createGameModalOpen}
        onOpenChange={(v) => {
          setCreateGameModalOpen(v);
          if (!v) setCreateGameForm({ name: "", date: "", formatId: 0, tournamentId: 0, isRated: false, tierId: null });
        }}
        createGameForm={createGameForm}
        setCreateGameForm={setCreateGameForm}
        gameTournaments={gameTournaments}
        gameFormats={gameFormats}
        ratingTiers={ratingTiers}
        loading={loading}
        creatingTournament={createTournamentLoading}
        onCreateTournament={handleCreateTournamentForOrgGame}
        onCreate={handleCreateOrgGame}
      />

      <OrganizerManagementDialog
        open={orgManageOpen}
        onOpenChange={setOrgManageOpen}
        currentUserId={user.data?.id}
        onStatusChange={() => {
          api.get("users/organizer/me")
            .then((res) => {
              const org = !!res.data?.isOrganizer;
              setIsOrganizer(org);
              if (org) {
                api.get("game-formats").then(r => setGameFormats(r.data)).catch(() => {});
                api.get("organizer-games/mine").then(r => setMyOrgGames(r.data)).catch(() => {});
                fetchOrganizerTournaments();
              } else {
                setMyOrgGames([]);
              }
            })
            .catch(() => setIsOrganizer(false));
        }}
      />

      {isFeatureEnabled('ACCOUNT_VERIFICATION') && (
        <VerificationDialog
          open={verificationDialogOpen}
          onOpenChange={setVerificationDialogOpen}
          onSubmitted={fetchVerificationRequest}
        />
      )}

      <EventDetailDialog
        open={!!detailEvent}
        onOpenChange={(v) => {
          if (!v) {
            setDetailEvent(null);
            setRegisterTeamId(null);
            setRegistrations([]);
          }
        }}
        detailEvent={detailEvent}
        user={user}
        profile={profile}
        ownProfile={ownProfile}
        registrations={registrations}
        isRegistered={isRegistered}
        registerTeamId={registerTeamId}
        setRegisterTeamId={setRegisterTeamId}
        registrationLoading={registrationLoading}
        onRegister={handleRegister}
      />
    </div>
  );
};

const mapStateToProps = ({ user, profile }: any) => ({ user, profile });

export default withRouter(
  connect(mapStateToProps, {
    loadingOn, loadingOff, setUserPhoto, getProfile, setProfile,
  })(Profile)
);
